import React, { useRef } from "react";
import { SelectState } from "@react-stately/select";
import { useListBox, useOption } from "@react-aria/listbox";
import { mergeProps } from "@react-aria/utils";
import { useFocus } from "@react-aria/interactions";
import { FocusScope } from "@react-aria/focus";
import { useOverlay, DismissButton } from "@react-aria/overlays";
import styled from "@emotion/styled";
import { theme } from "../theme";
import { Typography } from "../Typography";
import { ErrorStyles, DisabledStyles, getSelectedCSS } from "./Select";
import TickIcon from "@ecologi/assets/icons/FormFields/tick.svg";

export type ListboxProps = {
  state: SelectState<any>;
  hiddenLabel?: boolean;
  isInvalid?: boolean;
};

type ListBoxCSSProps = {
  isDisabled?: boolean;
  isFocused?: boolean;
  isInvalid?: boolean;
  isOpen?: boolean;
  hiddenLabel?: boolean;
  isSelected?: boolean;
};

const ListWrapper = styled.div<{ isOpen?: boolean }>`
  background-color: ${(props) => props.theme.colors.snowdrop};
`;

const List = styled.ul<ListBoxCSSProps>`
  position: absolute;
  top: ${(props) => (props.hiddenLabel ? "45px" : "77px")};
  z-index: 2;

  max-width: 890px;
  max-height: 300px;
  width: 100%;
  overflow-y: auto;

  padding: 0px;
  list-style: none;

  background-color: ${(props) => props.theme.colors.snowdrop};
  box-shadow: 4px 0px 10px #0000000d;
  outline: none;
  margin: 0;
  z-index: 99;

  &:focus {
    outline: none;
  }

  border: solid 2px ${theme.colors.charcoal};

  ${(props) => props.isDisabled && DisabledStyles};
  ${(props) => props.isFocused && getSelectedCSS(props)};
  ${(props) => props.isInvalid && props.isSelected && ErrorStyles}
`;

export function ListBoxPopup({ state, ...restProps }: ListboxProps) {
  const listBoxRef = useRef<HTMLUListElement>(null);

  // Get props for the listbox
  const { listBoxProps } = useListBox(
    {
      autoFocus: state.focusStrategy || true,
      disallowEmptySelection: true,
      ...restProps,
    },
    state,
    listBoxRef
  );

  // Handle events that should cause the popup to close,
  // e.g. blur, clicking outside, or pressing the escape key.
  const overlayRef = useRef<HTMLDivElement>(null);
  const { overlayProps } = useOverlay(
    {
      onClose: () => state.close(),
      shouldCloseOnBlur: true,
      isOpen: state.isOpen,
      isDismissable: true,
    },
    overlayRef
  );

  // Wrap in <FocusScope> so that focus is restored back to the
  // trigger when the popup is closed. In addition, add hidden
  // <DismissButton> components at the start and end of the list
  // to allow screen reader users to dismiss the popup easily.

  return (
    <FocusScope restoreFocus>
      <ListWrapper {...overlayProps} ref={overlayRef}>
        <DismissButton onDismiss={() => state.close()} />
        <List
          {...mergeProps(listBoxProps, restProps)}
          ref={listBoxRef}
          isOpen={state.isOpen}
          role="listbox"
        >
          {[...(state.collection as any)].map((item) => (
            <Option key={item.key} item={item} state={state} />
          ))}
        </List>
        <DismissButton onDismiss={() => state.close()} />
      </ListWrapper>
    </FocusScope>
  );
}

const ListItem = styled.li<ListBoxCSSProps>`
  background-color: ${(props) =>
    (props.isSelected && props.theme.colors.mist) ||
    (props.isFocused && props.theme.colors.mistLight)};
  color: ${(props) => props.theme.colors.charcoal};
  padding: 9px 16px;
  outline: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${(props) => (props.isSelected ? null : " #F9F9F9")};
  }
`;

export type OptionProps = {
  state: SelectState<any>;
  item: {
    key: string;
    rendered: string;
  };
};

function Option({ item, state }: OptionProps) {
  const listRef = useRef<HTMLLIElement>(null);
  const isDisabled = state.disabledKeys.has(item.key);
  const isSelected = state.selectionManager.isSelected(item.key);
  const { optionProps } = useOption(
    {
      key: item.key,
      isDisabled,
      isSelected,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: false,
    },
    state,
    listRef
  );

  // Handle focus events so we can apply highlighted
  // style to the focused option
  const [isFocused, setFocused] = React.useState(false);
  const { focusProps } = useFocus({ onFocusChange: setFocused });

  return (
    <>
      <ListItem
        {...mergeProps(optionProps, focusProps)}
        isSelected={isSelected}
        isFocused={isFocused}
        ref={listRef}
        role="option"
      >
        <Typography use="body1" as="p">
          {item.rendered}
        </Typography>
        {isSelected && <TickIcon />}
      </ListItem>
    </>
  );
}
